import {mdiCheck, mdiAlertOutline} from '@mdi/js'
import Icon from '@mdi/react'
import {Box, Typography} from '@mui/material'
import React from 'react'

import {FinalReviewItemProps, FinalReviewItemValue} from './FinalReviewItem.types'

/**
 * FinalReviewItem Component
 *
 * Displays a single field in the final review, showing:
 * - Label in grey
 * - Old values with strikethrough (if discarded)
 * - New values in green (no approval) or orange (requires approval)
 * - Checkmark icon for approved changes
 * - Warning icon for changes requiring approval
 *
 * @example
 * // Simple value (no change, just display)
 * <FinalReviewItem label="Mix type" value="PMP/30/40/60" />
 *
 * @example
 * // Changed value requiring approval
 * <FinalReviewItem
 *   label="Interval"
 *   value={[
 *     { text: "Spacing – 20 min", isDiscarded: true },
 *     { text: "Spacing - 15 min", isNew: true }
 *   ]}
 *   requiresApproval={true}
 * />
 *
 * @example
 * // New value added (no old value)
 * <FinalReviewItem
 *   label="Usage"
 *   value={[{ text: "Floor", isNew: true }]}
 *   requiresApproval={false}
 * />
 */
export const FinalReviewItem: React.FC<FinalReviewItemProps> = ({
  label,
  value,
  requiresApproval = false,
  testId
}) => {
  const isStringValue = typeof value === 'string'
  const values: FinalReviewItemValue[] = isStringValue
    ? [{text: value, isNew: false, isDiscarded: false}]
    : value

  const hasChanges = values.some((v) => v.isNew || v.isDiscarded)
  const approvalColor = requiresApproval ? '#ee8b00' : '#00a651'

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        gap: '4px'
      }}
      data-test-id={testId}
    >
      {/* Label */}
      <Typography
        sx={{
          fontSize: '12px',
          fontWeight: 400,
          lineHeight: '8px',
          color: '#52667a',
          fontFamily: 'Inter'
        }}
      >
        {label}
      </Typography>

      {/* Values Container */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'flex-start',
          gap: '8px',
          position: 'relative'
        }}
      >
        {/* Value(s) */}
        <Box
          sx={{
            flex: 1,
            display: 'flex',
            flexDirection: 'column',
            gap: '2px'
          }}
        >
          {values.map((val, index) => (
            <Typography
              key={index}
              sx={{
                fontSize: '15px',
                fontWeight: 500,
                lineHeight: '22px',
                color: val.isDiscarded ? '#29333d' : val.isNew ? approvalColor : '#29333d',
                fontFamily: 'Inter',
                textDecoration: val.isDiscarded ? 'line-through' : 'none',
                whiteSpace: 'pre-line',
                wordBreak: 'break-word'
              }}
            >
              {val.text}
            </Typography>
          ))}
        </Box>

        {/* Icon - only show if there are changes */}
        {hasChanges && (
          <Box
            sx={{
              flexShrink: 0,
              display: 'flex',
              alignItems: 'center',
              paddingTop: '2px' // Align with text baseline
            }}
          >
            <Icon
              path={requiresApproval ? mdiAlertOutline : mdiCheck}
              size={0.8}
              color={approvalColor}
              data-test-id={`${testId}-${requiresApproval ? 'warning' : 'check'}-icon`}
            />
          </Box>
        )}
      </Box>
    </Box>
  )
}
